import { Button, Text } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { memo, useCallback, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"

const DeleteUser = memo(({ id }: { id: string }) => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const mutation = useMutation({
    mutationFn: async () => {
      await api.admin.deleteUserApiV1AdminUsersUserIdDelete({ userId: id })
    },
    onSuccess: () => {
      showSuccessToast("The user was deleted successfully")
      queryClient.invalidateQueries()
    },
    onError: () => {
      showErrorToast("An error occurred while deleting the user")
    },
  })

  const handleConfirm = useCallback(async () => {
    await mutation.mutateAsync()
  }, [mutation])

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        colorPalette="red"
        onClick={() => setIsOpen(true)}
      >
        <FiTrash2 fontSize="16px" />
        Delete User
      </Button>

      <ConfirmDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onConfirm={handleConfirm}
        title="Delete User"
        confirmText="Delete"
        confirmColorScheme="red"
        isLoading={mutation.isPending}
        message={
          <Text>
            All items associated with this user will also be{" "}
            <strong>permanently deleted.</strong> Are you sure? You will not be
            able to undo this action.
          </Text>
        }
      />
    </>
  )
})

DeleteUser.displayName = "DeleteUser"

export default DeleteUser
